import { Badge, Flex, Icon, Small } from "orcs-design-system";

import {
  formatClockHours,
  liveElapsedHours,
  showPastSla,
} from "src/pages/WorkforceGuardrails/clockHours";

import { checkTitle, type RuleLabel } from "./guardrailRuleLabels";
import type {
  ApprovalGroup,
  ApprovalStep,
  GuardrailEvaluation,
} from "./RequisitionGuardrailOutcome";

const text = (value: string | null | undefined): string => value?.trim() ?? "";

const groupName = (group: ApprovalGroup): string =>
  text(group.approverGroupName) || "Approver group";

const groupSteps = (steps: ApprovalStep[]): ApprovalStep[] =>
  [...steps]
    .filter((step) => step.kind === "group")
    .sort((left, right) => left.order - right.order);

type ChipVariant = "primary" | "success" | "danger" | "default";

const chipVariant = (groups: ApprovalGroup[]): ChipVariant => {
  if (
    groups.some(
      (group) => group.canRespond === true && group.status === "PENDING"
    )
  ) {
    return "primary";
  }
  if (groups.some((group) => group.status === "REFUSED")) {
    return "danger";
  }
  if (
    groups.length > 0 &&
    groups.every((group) => group.status === "APPROVED")
  ) {
    return "success";
  }
  return "default";
};

const clockGroup = (groups: ApprovalGroup[]): ApprovalGroup | null =>
  groups.find(
    (group) => group.status === "PENDING" && text(group.clockStartedAt) !== ""
  ) ?? null;

const clockLine = (group: ApprovalGroup, nowMs: number): string | null => {
  const hours = liveElapsedHours(
    group.elapsedHours,
    group.clockStartedAt,
    nowMs
  );
  const elapsed = hours == null ? null : `Waiting ${formatClockHours(hours)}`;
  const sla =
    typeof group.slaHours === "number"
      ? `SLA ${formatClockHours(group.slaHours)}`
      : null;
  const parts = [elapsed, sla].filter((part): part is string => part != null);
  if (parts.length === 0) {
    return null;
  }
  return parts.join(" · ");
};

const addedRuleLine = (
  steps: ApprovalStep[],
  rules: RuleLabel[],
  loaded: boolean
): string | null => {
  const names: string[] = [];
  for (const step of groupSteps(steps)) {
    for (const group of step.groups ?? []) {
      for (const rule of group.addingRules ?? []) {
        const name = checkTitle(
          { name: rule.ruleName, ruleId: rule.ruleId },
          rules,
          loaded
        );
        if (name !== "" && !names.includes(name)) {
          names.push(name);
        }
      }
    }
  }
  if (names.length === 0) {
    return null;
  }
  return `Added by rule: ${names.join(", ")}`;
};

const routeLine = (
  template: GuardrailEvaluation["template"],
  failingNames: string[]
): string | null => {
  if (template !== "exception") {
    return null;
  }
  if (failingNames.length === 0) {
    return "Exception route";
  }
  return `Exception route — warnings: ${failingNames.join(", ")}`;
};

const ApprovalRoutingChips = ({
  steps,
  template,
  failingNames,
  nowMs,
  rules,
  loaded,
}: {
  steps: ApprovalStep[];
  template: GuardrailEvaluation["template"];
  failingNames: string[];
  nowMs: number;
  rules: RuleLabel[];
  loaded: boolean;
}) => {
  const chips = groupSteps(steps);
  if (chips.length === 0) {
    return null;
  }
  const note =
    addedRuleLine(steps, rules, loaded) ?? routeLine(template, failingNames);
  return (
    <Flex flexDirection="column" gap="s" data-testid="approval-routing">
      <Small uppercase weight="bold" color="greyDark">
        Approval routing
      </Small>
      <Flex flexWrap="wrap" alignItems="center" gap="s">
        {chips.map((step, index) => {
          const groups = step.groups ?? [];
          const live = clockGroup(groups);
          const line = live ? clockLine(live, nowMs) : null;
          const past = live
            ? showPastSla({
                slaHours: live.slaHours,
                pastSla: live.pastSla === true,
                elapsedHours: live.elapsedHours,
                clockStartedAt: live.clockStartedAt,
                nowMs,
              })
            : false;
          return (
            <Flex
              key={step.order}
              alignItems="center"
              gap="s"
              data-testid={`approval-chip-${step.order}`}
            >
              {index > 0 ? (
                <Icon icon="chevron-right" variant="grey" aria-hidden="true" />
              ) : null}
              <Flex flexDirection="column" gap="xs">
                <Badge variant={chipVariant(groups)}>
                  {groups.map(groupName).join(" / ")}
                </Badge>
                {line ? <Small color="greyDark">{line}</Small> : null}
                {past ? <Badge variant="danger">Past SLA</Badge> : null}
              </Flex>
            </Flex>
          );
        })}
      </Flex>
      {note ? <Small color="greyDark">{note}</Small> : null}
    </Flex>
  );
};

export default ApprovalRoutingChips;
